/**
 * Page Analytics Utilities
 *
 * Path normalization, category detection, and handle/event extraction
 * for the visitor analytics system.
 */

// Known static route prefixes — any first-segment path NOT in this set is treated as an artist handle
const STATIC_PREFIXES = new Set([
  'admin',
  'api',
  'how-it-works',
  'discover',
  'v',
  'embed',
  'login',
  'signup',
  'onboarding',
  'privacy',
  'terms',
  'ai-usage',
  'contact',
  'referrals',
  'demo',
  'invite',
  '_next',
  'favicon.ico',
])

interface PathInfo {
  normalizedPath: string
  pageCategory: string
  artistHandle?: string
  eventId?: string
}

/**
 * Normalize a raw URL path into a pattern path for aggregation,
 * and extract metadata (category, artist handle, event ID).
 */
export function normalizePath(path: string): PathInfo {
  // Remove query string and hash
  const cleanPath = path.split('?')[0].split('#')[0]
  const segments = cleanPath.split('/').filter(Boolean)

  // Root
  if (segments.length === 0) {
    return { normalizedPath: '/', pageCategory: 'marketing' }
  }

  const first = segments[0]

  // Static marketing pages
  if (first === 'how-it-works') {
    if (segments.length === 1) return { normalizedPath: '/how-it-works', pageCategory: 'marketing' }
    return { normalizedPath: `/how-it-works/${segments[1]}`, pageCategory: 'marketing' }
  }

  // Static pages (exact match)
  const staticPages = ['privacy', 'terms', 'ai-usage', 'contact', 'referrals', 'demo']
  if (staticPages.includes(first) && segments.length === 1) {
    return { normalizedPath: `/${first}`, pageCategory: 'marketing' }
  }

  // Discover
  if (first === 'discover') {
    return { normalizedPath: '/discover', pageCategory: 'discover' }
  }

  // Venue pages
  if (first === 'v') {
    if (segments.length === 1) return { normalizedPath: '/v', pageCategory: 'venue' }
    return { normalizedPath: '/v/[venueHandle]', pageCategory: 'venue' }
  }

  // Auth pages
  if (first === 'login' || first === 'signup' || first === 'onboarding') {
    return { normalizedPath: `/${first}`, pageCategory: 'auth' }
  }

  // Invite pages
  if (first === 'invite') {
    if (segments.length >= 2) return { normalizedPath: '/invite/[code]', pageCategory: 'marketing' }
    return { normalizedPath: '/invite', pageCategory: 'marketing' }
  }

  // Embed pages
  if (first === 'embed') {
    if (segments.length >= 2) {
      return {
        normalizedPath: '/embed/[handle]',
        pageCategory: 'embed',
        artistHandle: segments[1],
      }
    }
    return { normalizedPath: '/embed', pageCategory: 'embed' }
  }

  // Admin pages — should not be tracked, but normalize just in case
  if (first === 'admin') {
    return { normalizedPath: '/admin', pageCategory: 'admin' }
  }

  // If not a known static prefix, treat first segment as artist handle
  if (!STATIC_PREFIXES.has(first)) {
    const handle = first

    // /[handle]
    if (segments.length === 1) {
      return { normalizedPath: '/[handle]', pageCategory: 'artist', artistHandle: handle }
    }

    const second = segments[1]

    // /[handle]/dashboard/* — should not be tracked
    if (second === 'dashboard') {
      return { normalizedPath: '/[handle]/dashboard', pageCategory: 'dashboard', artistHandle: handle }
    }

    // /[handle]/events
    if (second === 'events') {
      return { normalizedPath: '/[handle]/events', pageCategory: 'artist', artistHandle: handle }
    }

    // /[handle]/live/[eventId]
    if (second === 'live' && segments.length >= 3) {
      return {
        normalizedPath: '/[handle]/live/[eventId]',
        pageCategory: 'event',
        artistHandle: handle,
        eventId: segments[2],
      }
    }

    // /[handle]/live (no event ID)
    if (second === 'live') {
      return { normalizedPath: '/[handle]/live', pageCategory: 'event', artistHandle: handle }
    }

    // /[handle]/merch, /[handle]/tip, etc.
    return {
      normalizedPath: `/[handle]/${second}`,
      pageCategory: 'artist',
      artistHandle: handle,
    }
  }

  // Fallback for any other static route
  return { normalizedPath: cleanPath, pageCategory: 'other' }
}

/**
 * Lightweight User-Agent parser. No heavy dependency — just regex matching
 * for the most common device types and browsers.
 */
export function parseUserAgent(ua: string): { deviceType: string; browserFamily: string } {
  // Check tablet before mobile — iPad UAs contain "Mobile" too
  let deviceType = 'desktop'
  if (/tablet|ipad/i.test(ua)) {
    deviceType = 'tablet'
  } else if (/mobile|android|iphone|ipod/i.test(ua)) {
    deviceType = 'mobile'
  }

  // Order matters: check Edge/Opera before Chrome (they include "Chrome" in UA),
  // check FxiOS before Safari (iOS Firefox includes "Safari" in UA)
  let browserFamily = 'Other'
  if (/edg\//i.test(ua)) browserFamily = 'Edge'
  else if (/opr\/|opera/i.test(ua)) browserFamily = 'Opera'
  else if (/firefox|fxios/i.test(ua)) browserFamily = 'Firefox'
  else if (/chrome|crios/i.test(ua)) browserFamily = 'Chrome'
  else if (/safari/i.test(ua)) browserFamily = 'Safari'

  return { deviceType, browserFamily }
}

/**
 * Compute a 0–100 engagement score from raw page-interaction signals.
 *
 * This is the single source of truth for the score, shared by the client
 * tracker (as a UI hint) and the server ingestion route. The server recomputes
 * it from validated inputs and NEVER trusts a client-supplied score (#257).
 *
 * Inputs are clamped to sane ranges so a forged payload cannot inflate the
 * result: scrollDepthMax is a percentage (0–100) and timeScore already
 * saturates at 2 minutes.
 */
export function computeEngagementScore(
  scrollDepthMax: number,
  timeOnPageMs: number,
  scrolledPast50: boolean
): number {
  const safeScroll = Math.min(Math.max(scrollDepthMax || 0, 0), 100) // 0-100
  const safeTime = Math.max(timeOnPageMs || 0, 0)
  const scrollScore = safeScroll
  const timeScore = Math.min(safeTime / 120000, 1) * 100 // 2 min = 100%
  const interactionScore = scrolledPast50 ? 100 : 0
  return scrollScore * 0.3 + timeScore * 0.5 + interactionScore * 0.2
}
